import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface ReferrerData {
  referrer: string;
  referrer_engine?: string;
  referrer_search?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  firstAccessUrl?: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class ReferrerTrackingService {
  private referrerData: ReferrerData | null = null;
  private readonly STORAGE_KEY = 'musaffa_referrer_data';

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.initializeReferrerTracking();
  }

  private initializeReferrerTracking(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.captureFirstAccessUrl();
      this.captureReferrerData();
    } 
  }

  private captureFirstAccessUrl(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      // Check if first access URL is already stored in the referrer data
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      let hasFirstAccessUrl = false;
      
      if (stored) {
        try {
          const storedData = JSON.parse(stored) as ReferrerData;
          hasFirstAccessUrl = !!storedData.firstAccessUrl;
        } catch {
          // If parsing fails, treat as no stored data
        }
      }
      
      if (!hasFirstAccessUrl) {
        // Store the current URL as the first access URL in a temporary object
        const currentUrl = window.location.href;
        const tempData: Partial<ReferrerData> = { firstAccessUrl: currentUrl };
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(tempData));
        console.log('First access URL captured:', currentUrl);
      }
    } catch (error) {
      console.error('Error capturing first access URL:', error);
    }
  }

  private getReferrerEngine = (referrer: string): string => {
    if(referrer.includes('google')) return 'google';
    if(referrer.includes('facebook')) return 'facebook';
    if(referrer.includes('twitter')) return 'twitter';
    if(referrer.includes('linkedin')) return 'linkedin';
    if(referrer.includes('instagram')) return 'instagram';
    if(referrer.includes('youtube')) return 'youtube';
    return 'other';
  }

  private getSearchQuery(referrer: string): string | undefined {
    try {
      const url = new URL(referrer);
      const params = new URLSearchParams(url.search);
      return params.get("q") || params.get("p") || undefined;
    } catch (e) {
      return undefined;
    }
  }

  private getParamValue = <K extends keyof ReferrerData>(
    key: K,
    urlParamKey?: string,
    defaultValue?: ReferrerData[K]
  ): ReferrerData[K] => {
    if (!isPlatformBrowser(this.platformId)) return defaultValue as ReferrerData[K];
    const stored = sessionStorage.getItem(this.STORAGE_KEY);
      let storedData: ReferrerData | null = null;
      const urlParams = new URLSearchParams(window.location.search);
      if (stored) {
        try {
          storedData = JSON.parse(stored) as ReferrerData;
        } catch {
          storedData = null;
        }
      }
      return (storedData?.[key] as ReferrerData[K])
        ?? (urlParamKey ? (urlParams.get(urlParamKey) as ReferrerData[K]) ?? undefined : undefined)
        ?? (defaultValue as ReferrerData[K]);
  };

  private captureReferrerData(): void {
    try {
      const currentOrigin = window.location.origin;
      const referrer: string = document.referrer;
      const isExternalReferrer =
        !!referrer &&
        (() => {
          try {
            const referrerUrl = new URL(referrer);
            return referrerUrl.origin !== currentOrigin;
          } catch {
            // If referrer is not a valid URL, treat it as external (rare, fallback)
            return true;
          }
        })();
      // Prefer sessionStorage value, then URL, then fallback.
      const directHosts = [
        'localhost:4200',
        'musaffa.com',
        'musaffa.swbeta.in',
        'beta.musaffa.com'
      ];
      let effectiveReferrer = referrer || 'direct';
      try {
        const refUrl = new URL(referrer);
        const hostMatches = directHosts.some(host =>
          refUrl.hostname === host || refUrl.host === host
        );
        if (hostMatches) {
          effectiveReferrer = 'direct';
        }
      } catch {
        // If parsing fails (referrer not a valid URL), keep the original referrer
      }
      const referrerUrl = this.getParamValue('referrer', undefined, effectiveReferrer);
      const firstAccessUrl = this.getFirstAccessUrl();
      const referrerData: ReferrerData = {
        referrer: referrerUrl,
        utmSource: this.getParamValue('utmSource', 'utm_source'),
        utmMedium: this.getParamValue('utmMedium', 'utm_medium'),
        utmCampaign: this.getParamValue('utmCampaign', 'utm_campaign'),
        utmTerm: this.getParamValue('utmTerm', 'utm_term'),
        utmContent: this.getParamValue('utmContent', 'utm_content'),
        referrer_engine: this.getReferrerEngine(referrerUrl),
        referrer_search: this.getSearchQuery(referrerUrl),
        firstAccessUrl: firstAccessUrl,
        timestamp: Date.now()
      };

      const isAnyValueSet = referrerData.utmSource
        || referrerData.utmMedium
        || referrerData.utmCampaign
        || referrerData.utmTerm
        || referrerData.utmContent
        || referrerData.referrer !== 'direct';

      // If user is coming from external link, store the new data
      // if (isExternalReferrer) {
      if(isAnyValueSet) {
        this.referrerData = referrerData;
        this.storeReferrerData(referrerData);
      }
      console.log('Referrer data:', this.referrerData);
    } catch (error) {
      console.error('Error capturing referrer data:', error);
    }
  }

  private storeReferrerData(data: ReferrerData): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error storing referrer data:', error);
    }
  }

  private getStoredReferrerData(): ReferrerData | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error retrieving referrer data from storage:', error);
    }
    return null;
  }

  private getFirstAccessUrl(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) return undefined;
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const storedData = JSON.parse(stored) as ReferrerData;
        return storedData.firstAccessUrl;
      }
    } catch (error) {
      console.error('Error retrieving first access URL from storage:', error);
    }
    return undefined;
  }

  getReferrerUrl(): string {
    const data = this.getReferrerData();
    if (!data) return 'direct';
    return data.referrer;
  }

  public getFirstAccessUrlFromSession(): string | undefined {
    return this.getFirstAccessUrl();
  }

  public getFirstAccessUrlFromReferrerData(): string | undefined {
    const data = this.getReferrerData();
    return data?.firstAccessUrl;
  }

  public getReferrerData(): ReferrerData | null {
    if (this.referrerData) {
      return this.referrerData;
    }

    if (!isPlatformBrowser(this.platformId)) return null;

    // Try to get from sessionStorage if not in memory
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.referrerData = JSON.parse(stored);
        return this.referrerData;
      }
    } catch (error) {
      console.error('Error retrieving referrer data from storage:', error);
    }

    return null;
  }

  public clearReferrerData(): void {
    this.referrerData = null;
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing referrer data:', error);
    }
  }

  public getReferrerSource(): string {
    const data = this.getReferrerData();
    if (!data) return 'direct';

    // Determine the source based on referrer and UTM parameters
    if (data.utmSource) {
      return data.utmSource;
    }

    // if (data.referrer) {
    //   try {
    //     const url = new URL(data.referrer);
    //     const hostname = url.hostname.toLowerCase();
        
    //     // Common referrer sources
    //     if (hostname.includes('google')) return 'google';
    //     if (hostname.includes('facebook')) return 'facebook';
    //     if (hostname.includes('twitter')) return 'twitter';
    //     if (hostname.includes('linkedin')) return 'linkedin';
    //     if (hostname.includes('instagram')) return 'instagram';
    //     if (hostname.includes('youtube')) return 'youtube';
    //     if (hostname.includes('bing')) return 'bing';
    //     if (hostname.includes('yahoo')) return 'yahoo';
    //     if (hostname.includes('duckduckgo')) return 'duckduckgo';
        
    //     // Return the hostname for other sources
    //     return hostname;
    //   } catch (error) {
    //     return data.referrer;
    //   }
    // }

    return 'direct';
  }

  public getReferrerMedium(): string {
    const data = this.getReferrerData();
    if (!data) return 'none';

    if (data.utmMedium) {
      return data.utmMedium;
    }

    // if (data.referrer) {
    //   try {
    //     const url = new URL(data.referrer);
    //     const hostname = url.hostname.toLowerCase();
        
    //     // Determine medium based on hostname
    //     if (hostname.includes('google') || hostname.includes('bing') || hostname.includes('yahoo') || hostname.includes('duckduckgo')) {
    //       return 'organic';
    //     }
    //     if (hostname.includes('facebook') || hostname.includes('twitter') || hostname.includes('linkedin') || hostname.includes('instagram')) {
    //       return 'social';
    //     }
    //     if (hostname.includes('youtube')) {
    //       return 'video';
    //     }
        
    //     return 'referral';
    //   } catch (error) {
    //     return 'referral';
    //   }
    // }

    return 'none';
  }
}
